// Phase 7-D · Meet Levi first-run flow (5 screens).
// Renders inline in the LeviAssistantDrawer when the caller's
// `user.levi_preferences.levi_preferences_setup_done` is false.
// Every screen has a "Skip for now" button — any skip stamps
// setup_done=true via the reset endpoint so Meet Levi never
// reopens automatically. Each Next click writes its chosen field
// immediately (idempotent), preserving whatever was set even if
// the contractor closes the drawer mid-flow.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Play, Volume2 } from "lucide-react";

const L = {
  en: {
    welcome_title: "Hi — I'm Levi.",
    welcome_body: "I'm your project manager. I keep the paperwork moving, follow up on customers, and keep your day on track.",
    welcome_choice_title: "How would you like to get set up?",
    setup_with_levi: "Set up with Levi",
    setup_with_levi_desc: "Talk with me and I'll walk you through your business and pricing setup.",
    setup_manually: "Set up manually",
    setup_manually_desc: "Enter your business and pricing information yourself.",
    welcome_lets_go: "Let's set up",
    skip: "Skip for now",
    name_title: "What should I call you?",
    name_body: "Just a first name — I'll only use it on greetings. Your full name stays on every document.",
    name_placeholder: "e.g., Sam",
    voice_title: "How would you like me to sound?",
    voice_professional: "Professional",
    voice_professional_desc: "Grounded, steady, project-manager delivery.",
    voice_warm: "Warm",
    voice_warm_desc: "Softer cadence — like a trusted coworker.",
    preview: "Preview",
    preview_loading: "Loading…",
    preview_unavailable: "Preview unavailable — try again in a moment.",
    style_title: "How should I communicate?",
    style_brief: "Brief and direct",
    style_brief_desc: "Get to the point. Minimal preamble.",
    style_balanced: "Balanced",
    style_balanced_desc: "Clear with just enough context.",
    style_conversational: "Conversational",
    style_conversational_desc: "Explains reasoning. Truck-cab chat feel.",
    speed_title: "How fast should I speak?",
    speed_slow: "Slow",
    speed_normal: "Normal",
    speed_fast: "Fast",
    read_aloud_title: "Read my responses aloud",
    read_aloud_body: "You'll still see the text — this just controls whether I speak.",
    confirm_title: "You're all set.",
    confirm_body: "You can change any of this from Levi Settings.",
    confirm_finish: "Start using Levi",
    open_settings: "Open Levi Settings",
    back: "Back",
    next: "Next",
    saving: "Saving…",
    error: "Something went wrong. Try again.",
  },
  es: {
    welcome_title: "Hola — soy Levi.",
    welcome_body: "Soy tu gerente de proyectos. Mantengo el papeleo al día, doy seguimiento a los clientes y hago que tu día avance.",
    welcome_choice_title: "¿Cómo prefieres configurar?",
    setup_with_levi: "Configurar con Levi",
    setup_with_levi_desc: "Habla conmigo y te guío por tu negocio y precios.",
    setup_manually: "Configurar manualmente",
    setup_manually_desc: "Ingresa la información de tu negocio y precios tú mismo.",
    welcome_lets_go: "Configurar",
    skip: "Omitir por ahora",
    name_title: "¿Cómo debería llamarte?",
    name_body: "Solo tu primer nombre — lo uso en saludos. Tu nombre completo sigue apareciendo en cada documento.",
    name_placeholder: "ej., Sam",
    voice_title: "¿Cómo quieres que suene?",
    voice_professional: "Profesional",
    voice_professional_desc: "Firme, estable, tono de jefe de proyecto.",
    voice_warm: "Cálido",
    voice_warm_desc: "Cadencia más suave — como un compañero de confianza.",
    preview: "Escuchar",
    preview_loading: "Cargando…",
    preview_unavailable: "Muestra no disponible — inténtalo en un momento.",
    style_title: "¿Cómo debería comunicarme?",
    style_brief: "Breve y directo",
    style_brief_desc: "Al grano. Sin rodeos.",
    style_balanced: "Equilibrado",
    style_balanced_desc: "Claro con el contexto justo.",
    style_conversational: "Conversacional",
    style_conversational_desc: "Explica el porqué. Sensación de plática en la troca.",
    speed_title: "¿Qué tan rápido debería hablar?",
    speed_slow: "Lento",
    speed_normal: "Normal",
    speed_fast: "Rápido",
    read_aloud_title: "Leer respuestas en voz alta",
    read_aloud_body: "Seguirás viendo el texto — esto solo controla si hablo.",
    confirm_title: "¡Todo listo!",
    confirm_body: "Puedes cambiar cualquiera de estos en Configuración de Levi.",
    confirm_finish: "Empezar a usar Levi",
    open_settings: "Abrir Configuración de Levi",
    back: "Atrás",
    next: "Siguiente",
    saving: "Guardando…",
    error: "Algo salió mal. Inténtalo de nuevo.",
  },
};

const Tile = ({ selected, onClick, title, description, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500
      ${selected
        ? "border-orange-500 bg-orange-50"
        : "border-slate-200 bg-white hover:border-slate-300"}`}
  >
    <div className="flex items-center gap-2">
      <span className={`inline-block h-3 w-3 rounded-full border-2 ${selected ? "border-orange-500 bg-orange-500" : "border-slate-300"}`} />
      <span className="font-semibold text-slate-900">{title}</span>
    </div>
    {description && (
      <p className="text-sm text-slate-600 mt-1 pl-5">{description}</p>
    )}
  </button>
);

const SKIP_KEY = "jc:levi:7d:meet_skipped";

export default function MeetLeviFlow({ onDone }) {
  const { lang } = useLang();
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const t = L[lang === "es" ? "es" : "en"];
  const initial = user?.levi_preferences || {};
  const [step, setStep] = useState(0); // 0..5 (welcome, name, voice, style, speed+read_aloud, confirm)
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const audioRef = useRef(null);
  const cacheRef = useRef({});

  const [name, setName] = useState(initial.levi_preferred_name || "");
  const [voiceId, setVoiceId] = useState(initial.levi_voice_id || "professional");
  const [style, setStyle] = useState(initial.levi_communication_style || "balanced");
  const [speed, setSpeed] = useState(initial.levi_speaking_speed || "normal");
  const [readAloud, setReadAloud] = useState(
    initial.levi_read_aloud_enabled === null
      || initial.levi_read_aloud_enabled === undefined
      ? true
      : !!initial.levi_read_aloud_enabled,
  );

  const savePartial = async (partial) => {
    try {
      setSaving(true);
      await api.put("/user/levi-preferences", partial);
      try { refreshMe && refreshMe(); } catch { /* noop */ }
    } catch {
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  const skipAll = async () => {
    try {
      setSaving(true);
      await api.post("/user/levi-preferences/reset");
      try {
        localStorage.setItem(SKIP_KEY, "1");
      } catch { /* noop */ }
      try { refreshMe && refreshMe(); } catch { /* noop */ }
      onDone && onDone({ skipped: true });
    } catch {
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  // PO 2026-02 · First-login entry UX (Phase-5B). Give the new
  // contractor a clear choice between the existing Levi conversational
  // surface and the existing manual setup wizard. Both paths mark
  // levi_preferences_setup_done=true via the SAME reset endpoint that
  // "Skip for now" already uses — no new writer, no new lifecycle. The
  // manual path additionally closes the drawer (via a shared custom
  // event to avoid a circular import with LeviLauncher) and navigates
  // to the existing /app/setup route.
  const beginLeviConversation = async () => {
    try {
      setSaving(true);
      await api.post("/user/levi-preferences/reset");
      try { refreshMe && refreshMe(); } catch { /* noop */ }
      // Do NOT stamp SKIP_KEY — the contractor is engaging with Levi,
      // not skipping onboarding. The drawer stays open; the parent
      // ternary in LeviLauncher will now render LeviCommandConsole
      // because setup_done flipped to true.
      onDone && onDone({ skipped: false, chose: "levi" });
    } catch {
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  const beginManualSetup = async () => {
    try {
      setSaving(true);
      await api.post("/user/levi-preferences/reset");
      try { refreshMe && refreshMe(); } catch { /* noop */ }
      onDone && onDone({ skipped: false, chose: "manual" });
      try {
        window.dispatchEvent(new Event("levi:close-drawer"));
      } catch { /* noop */ }
      navigate("/app/setup");
    } catch {
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    try {
      setSaving(true);
      // Persist read-aloud too since the toggle was surfaced.
      await api.put("/user/levi-preferences", {
        levi_read_aloud_enabled: !!readAloud,
      });
      try {
        localStorage.setItem("jc:levi:tts-enabled", readAloud ? "1" : "0");
      } catch { /* noop */ }
      try { refreshMe && refreshMe(); } catch { /* noop */ }
      onDone && onDone({ skipped: false });
    } catch {
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  const preview = async (vid) => {
    setPreviewing(true);
    try {
      const key = `${vid}:${lang}:greeting`;
      let url = cacheRef.current[key];
      if (!url) {
        const token = (() => {
          try { return localStorage.getItem("jc_token") || ""; }
          catch { return ""; }
        })();
        const apiBase = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${apiBase}/api/levi/tts/preview`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            voice_id: vid,
            language: lang === "es" ? "es" : "en",
            sample_text_key: "greeting",
          }),
        });
        if (!r.ok) throw new Error(`http_${r.status}`);
        const blob = await r.blob();
        url = URL.createObjectURL(blob);
        cacheRef.current[key] = url;
      }
      try {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
      } catch { /* noop */ }
      const audio = new Audio(url);
      audioRef.current = audio;
      await audio.play();
    } catch {
      toast.error(t.preview_unavailable);
    } finally {
      setPreviewing(false);
    }
  };

  const totalSteps = 6;

  const Nav = ({ onNext, nextLabel, canBack = true, hideNext = false }) => (
    <div className="flex items-center justify-between gap-2 pt-4 border-t border-slate-200">
      <button
        type="button"
        data-testid="meetlevi-skip-btn"
        onClick={skipAll}
        disabled={saving}
        className="text-xs text-slate-500 hover:text-slate-700 underline"
      >
        {t.skip}
      </button>
      <div className="flex items-center gap-2">
        {canBack && step > 0 && (
          <button
            type="button"
            data-testid="meetlevi-back-btn"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={saving}
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            <ArrowLeft className="h-4 w-4" /> {t.back}
          </button>
        )}
        {!hideNext && (
          <button
            type="button"
            data-testid="meetlevi-next-btn"
            onClick={onNext}
            disabled={saving}
            className="inline-flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-60"
          >
            {saving
              ? <><Loader2 className="animate-spin h-4 w-4" /> {t.saving}</>
              : <>{nextLabel || t.next} <ArrowRight className="h-4 w-4" /></>}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="meet-levi-flow">
      {/* progress dots */}
      <div className="flex items-center gap-1.5">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-orange-500" : "w-1.5 bg-slate-300"}`}
          />
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-4" data-testid="meetlevi-step-welcome">
          <h3 className="text-xl font-bold text-slate-900">{t.welcome_title}</h3>
          <p className="text-sm text-slate-700 leading-relaxed">{t.welcome_body}</p>
          <p className="text-sm font-semibold text-slate-900 pt-2">
            {t.welcome_choice_title}
          </p>

          {/* PRIMARY · Set up with Levi — enters existing conversational
              surface via the parent ternary flip in LeviLauncher. */}
          <button
            type="button"
            data-testid="meetlevi-choose-levi-btn"
            onClick={beginLeviConversation}
            disabled={saving}
            className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 font-semibold text-left disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <div className="flex items-center gap-2">
              {saving ? (
                <Loader2 className="animate-spin h-4 w-4" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              <span>{t.setup_with_levi}</span>
            </div>
            <p className="text-xs text-orange-50 mt-1 pl-6 font-normal leading-snug">
              {t.setup_with_levi_desc}
            </p>
          </button>

          {/* SECONDARY · Set up manually — routes to existing /app/setup
              wizard. Pricing writes remain exclusively canonical. */}
          <button
            type="button"
            data-testid="meetlevi-choose-manual-btn"
            onClick={beginManualSetup}
            disabled={saving}
            className="w-full rounded-xl border-2 border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 text-slate-900 px-4 py-3 font-semibold text-left disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <div className="flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-slate-500" />
              <span>{t.setup_manually}</span>
            </div>
            <p className="text-xs text-slate-600 mt-1 pl-6 font-normal leading-snug">
              {t.setup_manually_desc}
            </p>
          </button>

          {/* TERTIARY · Skip for now — existing behavior. */}
          <div className="pt-1 flex justify-center">
            <button
              type="button"
              data-testid="meetlevi-skip-btn"
              onClick={skipAll}
              disabled={saving}
              className="text-sm text-slate-500 hover:text-slate-700 underline"
            >
              {t.skip}
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4" data-testid="meetlevi-step-name">
          <h3 className="text-xl font-bold text-slate-900">{t.name_title}</h3>
          <p className="text-sm text-slate-700">{t.name_body}</p>
          <input
            data-testid="meetlevi-name-input"
            type="text"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            value={name}
            placeholder={t.name_placeholder}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
          />
          <Nav onNext={async () => {
            await savePartial({ levi_preferred_name: name || null });
            setStep(2);
          }} />
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="meetlevi-step-voice">
          <h3 className="text-xl font-bold text-slate-900">{t.voice_title}</h3>
          <div className="grid grid-cols-1 gap-2">
            <Tile
              selected={voiceId === "professional"}
              onClick={() => setVoiceId("professional")}
              title={t.voice_professional}
              description={t.voice_professional_desc}
              testid="meetlevi-voice-professional"
            />
            <Tile
              selected={voiceId === "warm"}
              onClick={() => setVoiceId("warm")}
              title={t.voice_warm}
              description={t.voice_warm_desc}
              testid="meetlevi-voice-warm"
            />
          </div>
          <button
            type="button"
            data-testid="meetlevi-preview-btn"
            onClick={() => preview(voiceId)}
            disabled={previewing}
            className="inline-flex items-center gap-2 text-sm border border-slate-300 rounded-full px-3 py-1 hover:bg-slate-50 disabled:opacity-60"
          >
            {previewing
              ? <><Loader2 className="animate-spin h-3.5 w-3.5" /> {t.preview_loading}</>
              : <><Play className="h-3.5 w-3.5" /> {t.preview}</>}
          </button>
          <Nav onNext={async () => {
            await savePartial({ levi_voice_id: voiceId });
            setStep(3);
          }} />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4" data-testid="meetlevi-step-style">
          <h3 className="text-xl font-bold text-slate-900">{t.style_title}</h3>
          <div className="grid grid-cols-1 gap-2">
            <Tile
              selected={style === "brief"}
              onClick={() => setStyle("brief")}
              title={t.style_brief}
              description={t.style_brief_desc}
              testid="meetlevi-style-brief"
            />
            <Tile
              selected={style === "balanced"}
              onClick={() => setStyle("balanced")}
              title={t.style_balanced}
              description={t.style_balanced_desc}
              testid="meetlevi-style-balanced"
            />
            <Tile
              selected={style === "conversational"}
              onClick={() => setStyle("conversational")}
              title={t.style_conversational}
              description={t.style_conversational_desc}
              testid="meetlevi-style-conversational"
            />
          </div>
          <Nav onNext={async () => {
            await savePartial({ levi_communication_style: style });
            setStep(4);
          }} />
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4" data-testid="meetlevi-step-speed">
          <h3 className="text-xl font-bold text-slate-900">{t.speed_title}</h3>
          <div className="grid grid-cols-3 gap-2">
            {["slow", "normal", "fast"].map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`meetlevi-speed-${s}`}
                onClick={() => setSpeed(s)}
                className={`rounded-lg border-2 px-3 py-2 text-sm font-medium
                  ${speed === s
                    ? "border-orange-500 bg-orange-50 text-orange-800"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
              >
                {t[`speed_${s}`]}
              </button>
            ))}
          </div>
          <label className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-4 py-3">
            <div>
              <div className="font-semibold text-slate-800 flex items-center gap-2">
                <Volume2 className="h-4 w-4" /> {t.read_aloud_title}
              </div>
              <div className="text-xs text-slate-500">{t.read_aloud_body}</div>
            </div>
            <input
              data-testid="meetlevi-read-aloud"
              type="checkbox"
              className="h-5 w-5 accent-orange-500"
              checked={readAloud}
              onChange={(e) => setReadAloud(e.target.checked)}
            />
          </label>
          <Nav onNext={async () => {
            await savePartial({ levi_speaking_speed: speed });
            setStep(5);
          }} />
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4" data-testid="meetlevi-step-confirm">
          <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Check className="h-6 w-6 text-orange-500" /> {t.confirm_title}
          </h3>
          <p className="text-sm text-slate-700">{t.confirm_body}</p>
          <div className="text-sm bg-slate-50 rounded-lg p-3 space-y-1">
            <div><span className="text-slate-500">•</span> {name || (initial.preferred_name_effective || "")}</div>
            <div><span className="text-slate-500">•</span> {voiceId === "professional" ? t.voice_professional : t.voice_warm}</div>
            <div><span className="text-slate-500">•</span> {t[`style_${style}`]}</div>
            <div><span className="text-slate-500">•</span> {t[`speed_${speed}`]}</div>
          </div>
          <Link
            to="/app/settings/levi"
            data-testid="meetlevi-open-settings-link"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 underline"
          >
            {t.open_settings} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Nav
            onNext={finish}
            nextLabel={t.confirm_finish}
          />
        </div>
      )}
    </div>
  );
}
